// TestPage.js - Page de Test
import React, { useState, useEffect } from 'react';
import DataTable from '../components/DataTable';
import ConferenceFilterPanel from '../components/ConferenceFilterPanel';
import DefineFilterModal from '../components/DefineFilterModal';
import ConferenceCard from '../components/ConferenceCard';
import './DatabasePage.css';
import './TestPage.css';
import axios from 'axios';
import { conferenceFiltersService } from '../services/conferenceFiltersService';
import { attendedConferencesService } from '../services/attendedConferencesService';
import { getUserId } from '../utils/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import WelcomeModal from '../components/WelcomeModal';
// ⚠️ Imports retirés car non utilisés pour éviter warnings/bundles inutiles :
// import ArticleCard from '../components/ArticleCard';
// import '../components/ArticleCard.css';
// import { readArticleService } from '../services/api';
// import ArticleDetail from '../components/ArticleDetail';
// import { ToastManager } from '../components/ToastManager';
// import { getCurrentUser } from '../utils/auth';
// import { FilterPreferencesContext } from '../App';

const defaultConferenceFilters = {
  Country: [],
  Price: [],
  presence: [],
  type: [],
  year: [],
  query: '',
  attendingOnly: false
};

const TestPage = ({ viewMode = 'list' }) => {
  const [filtersVisible, setFiltersVisible] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => ({ ...defaultConferenceFilters }));
  const [filterOptions, setFilterOptions] = useState({
    Country: [],
    Price: [],
    presence: ['On Site', 'Online'],
    type: [],
    year: []
  });
  const [conferences, setConferences] = useState([]);
  const [attendedConferenceMap, setAttendedConferenceMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [defineModalOpen, setDefineModalOpen] = useState(false);
  const [savingDefineFilter, setSavingDefineFilter] = useState(false);
  const [defineFilterError, setDefineFilterError] = useState('');

  const [savedFilters, setSavedFilters] = useState([]);
  const [loadingSavedFilters, setLoadingSavedFilters] = useState(false);

  // Charger les options dynamiques pour les filtres
  useEffect(() => {
    axios
      .get('http://localhost:5001/api/conferences?startDate=2000-01-01&endDate=2100-12-31')
      .then(res => {
        if (res.data?.success) {
          const allConfs = res.data.conferences || [];
          setFilterOptions({
            Country: Array.from(new Set(allConfs.map(c => c.Country).filter(Boolean))),
            Price: Array.from(new Set(allConfs.map(c => c.Price).filter(Boolean))),
            presence: ['On Site', 'Online'],
            type: Array.from(new Set(allConfs.map(c => c.type).filter(Boolean))),
            year: Array.from(new Set(allConfs.map(c => c.year).filter(Boolean)))
          });
        } else {
          setFilterOptions({
            Country: [],
            Price: [],
            presence: ['On Site', 'Online'],
            type: [],
            year: []
          });
        }
      })
      .catch(() => {
        setFilterOptions({
          Country: [],
          Price: [],
          presence: ['On Site', 'Online'],
          type: [],
          year: []
        });
      });
  }, []);

  // Charger la liste selon les filtres
  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = [];
    if (filters.Country.length > 0) filters.Country.forEach(c => params.push(`country=${encodeURIComponent(c)}`));
    if (filters.Price.length > 0) filters.Price.forEach(p => params.push(`price=${encodeURIComponent(p)}`));
    if (filters.presence.length > 0) filters.presence.forEach(t => params.push(`presence=${encodeURIComponent(t)}`));
    if (filters.type.length > 0) filters.type.forEach(t => params.push(`type=${encodeURIComponent(t)}`));
    if (filters.year.length > 0) filters.year.forEach(y => params.push(`year=${encodeURIComponent(y)}`));
    if (filters.query) params.push(`query=${encodeURIComponent(filters.query)}`);
    if (filters.attendingOnly) params.push('attendingOnly=true');

    const userId = getUserId();
    if (userId) {
      params.push(`userId=${encodeURIComponent(userId)}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `http://localhost:5001/api/conferences${queryString}`;

    axios
      .get(url)
      .then(res => {
        if (res.data?.success) {
          const conferencesData = Array.isArray(res.data.conferences) ? res.data.conferences : [];
          const attendedIds = Array.isArray(res.data.attendedConferenceIds)
            ? res.data.attendedConferenceIds.map(id => String(id))
            : [];
          const attendanceSet = new Set(attendedIds);
          const normalizedConferences = conferencesData.map(conf => {
            const rawId = conf._id || conf.id || '';
            const normalizedId = rawId ? String(rawId) : '';
            return {
              ...conf,
              _id: normalizedId,
              isAttending: typeof conf.isAttending === 'boolean' ? conf.isAttending : attendanceSet.has(normalizedId)
            };
          });
          const attendanceMap = {};
          attendanceSet.forEach(id => {
            attendanceMap[id] = true;
          });
          setAttendedConferenceMap(attendanceMap);
          setConferences(normalizedConferences);
        } else {
          setError('Erreur lors du chargement des conferences');
        }
      })
      .catch(err => {
        setError('Erreur de connexion au serveur : ' + err.message);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const updateFilter = (filterName, value) => {
    if (filterName === 'attendingOnly' && value) {
      const userId = getUserId();
      if (!userId) {
        window.alert('Vous devez etre connecte pour utiliser ce filtre.');
        return;
      }
    }
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const normalizeSelectedFilters = selected => ({
    Country: Array.from(new Set(((selected?.Country) || []).map(String))),
    Price: Array.from(new Set(((selected?.Price) || []).map(String))),
    presence: Array.from(new Set(((selected?.presence) || []).map(String))),
    type: Array.from(new Set(((selected?.type) || []).map(String))),
    year: Array.from(new Set(((selected?.year) || []).map(String))),
    query: typeof selected?.query === 'string' ? selected.query : '',
    attendingOnly: Boolean(selected?.attendingOnly)
  });

  const getSavedFilterKey = filter => (filter?._id ? String(filter._id) : (filter?.name || ''));

  // Charger les filtres enregistrés
  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setSavedFilters([]);
      return;
    }

    let isMounted = true;
    setLoadingSavedFilters(true);

    conferenceFiltersService
      .getUserFilters(userId)
      .then(response => {
        if (!isMounted) return;
        const fetched = Array.isArray(response?.filters) ? response.filters : [];
        const normalized = fetched.map(item => ({
          ...item,
          filters: normalizeSelectedFilters(item.filters)
        }));
        setSavedFilters(normalized);
      })
      .catch(err => {
        if (!isMounted) return;
        console.error('Erreur lors du chargement des filtres sauvegardés:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingSavedFilters(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenDefineFilter = () => {
    setDefineFilterError('');
    setDefineModalOpen(true);
  };

  const handleCloseDefineFilter = () => {
    if (savingDefineFilter) return;
    setDefineModalOpen(false);
  };

  const handleResetFilters = () => {
    setFilters({ ...defaultConferenceFilters });
  };

  const handleToggleConferenceAttendance = async (conference, attending) => {
    const conferenceId = conference?._id || conference?.id;
    const userId = getUserId();
    if (!conferenceId) {
      return;
    }
    if (!userId) {
      window.alert('Vous devez etre connecte pour enregistrer vos conferences.');
      return;
    }

    try {
      const response = await attendedConferencesService.setAttendance(userId, conferenceId, attending);
      const attendedIds = Array.isArray(response?.attendedConferenceIds)
        ? response.attendedConferenceIds.map(id => String(id))
        : [];
      const attendanceMap = {};
      attendedIds.forEach(id => {
        attendanceMap[id] = true;
      });
      setAttendedConferenceMap(attendanceMap);
      setConferences(prev =>
        prev.map(conf => {
          const id = String(conf._id || conf.id || '');
          return {
            ...conf,
            isAttending: attendanceMap[id] || false
          };
        })
      );
    } catch (error) {
      console.error('Erreur lors de la mise a jour de la participation:', error);
      window.alert('Impossible de mettre a jour la participation. Veuillez reessayer.');
    }
  };

  const handleSaveDefinedFilter = async ({ name, filters: selectedFilters }) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      setDefineFilterError('Veuillez saisir un nom de filtre.');
      return;
    }

    const userId = getUserId();
    if (!userId) {
      setDefineFilterError('Vous devez être connecté pour enregistrer un filtre.');
      return;
    }

    const sanitizedFilters = normalizeSelectedFilters(selectedFilters);

    setSavingDefineFilter(true);
    setDefineFilterError('');

    try {
      const saveResponse = await conferenceFiltersService.save(userId, trimmedName, sanitizedFilters);
      const rawSavedFilter = saveResponse?.filter || null;
      const normalizedSavedFilter = rawSavedFilter
        ? { ...rawSavedFilter, filters: normalizeSelectedFilters(rawSavedFilter.filters) }
        : { name: trimmedName, filters: sanitizedFilters };

      // Appliquer les filtres sauvegardés à l'écran
      setFilters(prev => ({
        ...prev,
        ...sanitizedFilters
      }));

      // Mettre à jour la liste des filtres sauvegardés (upsert)
      setSavedFilters(prev => {
        const existing = prev.find(item =>
          getSavedFilterKey(item) === getSavedFilterKey(normalizedSavedFilter) || item.name === trimmedName
        );
        const enrichedFilter = {
          ...normalizedSavedFilter,
          _id: normalizedSavedFilter._id || existing?._id,
          createdAt: normalizedSavedFilter.createdAt || existing?.createdAt,
          updatedAt: normalizedSavedFilter.updatedAt || existing?.updatedAt
        };
        const keyToKeep = getSavedFilterKey(enrichedFilter);
        const remaining = prev.filter(item => getSavedFilterKey(item) !== keyToKeep);
        return [enrichedFilter, ...remaining];
      });

      setDefineModalOpen(false);
    } catch (saveError) {
      const errorData = saveError?.response?.data;
      const apiMessage = errorData?.error;
      const apiDetails = errorData?.details;
      const fallback = saveError?.message;
      const finalMessage = apiDetails
        ? `${apiMessage || 'Erreur serveur'} : ${apiDetails}`
        : apiMessage || fallback || 'Enregistrement impossible. Veuillez réessayer.';
      setDefineFilterError(finalMessage);
    } finally {
      setSavingDefineFilter(false);
    }
  };

  const handleDeleteSavedFilter = async (filterToDelete) => {
    const filterKey = getSavedFilterKey(filterToDelete);
    if (!filterKey) return;

    const userId = getUserId();
    if (!userId) {
      window.alert('Vous devez être connecté pour supprimer un filtre.');
      return;
    }

    const confirmed = window.confirm(`Supprimer le filtre "${filterToDelete?.name || ''}" ?`);
    if (!confirmed) return;

    try {
      if (filterToDelete?._id) {
        await conferenceFiltersService.delete(filterToDelete._id, userId);
      }
      setSavedFilters(prev => prev.filter(item => getSavedFilterKey(item) !== filterKey));
    } catch (deleteError) {
      console.error('Erreur lors de la suppression du filtre défini :', deleteError);
      window.alert('Impossible de supprimer ce filtre pour le moment.');
    }
  };

  const handleApplySavedFilter = savedFilter => {
    if (!savedFilter || !savedFilter.filters) return;
    const normalized = normalizeSelectedFilters(savedFilter.filters);
    setFilters(prev => ({
      ...prev,
      ...normalized
    }));
  };

  return (
    <div className="database-page">
      <WelcomeModal open={false} />
      <div className="database-header">
        <h1>Base de données de conférences et webinars</h1>
        <p>Trouvez les conférences et webinars qui vont vous permettre d&apos;approfondir vos connaissances</p>
      </div>

      <div className="database-content">
        <div className="filters-container">
          <div className={filtersVisible ? 'filter-panel' : 'filter-panel-collapsed'} style={{ position: 'relative' }}>
            {filtersVisible ? (
              <div style={{ position: 'absolute', top: 10, right: 16, zIndex: 10 }}>
                <button
                  title="Réduire les filtres"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
                  onClick={() => setFiltersVisible(false)}
                >
                  
                  <span role="img" aria-label="hide">🔽</span>
                </button>
              </div>
            ) : (
              <div
                className="filter-panel-toggle"
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                <button
                  title="Afficher les filtres"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 16,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  onClick={() => setFiltersVisible(true)}
                >
                  <span style={{ fontWeight: 500, fontSize: 16 }}>Afficher les filtres</span>
                  <span role="img" aria-label="show">🔼</span>
                </button>
              </div>
            )}

            {filtersVisible && (
              <>
                <ConferenceFilterPanel
                  options={filterOptions}
                  activeFilters={filters}
                  onFilterChange={updateFilter}
                />
                <div className="filter-panel-actions">
                  <button type="button" className="reset-filters-btn" onClick={handleResetFilters}>
                    Réinitialiser les filtres
                  </button>
                  <button type="button" className="define-filter-btn" onClick={handleOpenDefineFilter}>
                    Définir mon filtre
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {loadingSavedFilters ? (
          <div className="saved-filters-bar saved-filters-bar--loading">
            Chargement de vos filtres enregistrés...
          </div>
        ) : savedFilters.length > 0 ? (
          <div className="saved-filters-bar">
            <span className="saved-filters-label">Mes filtres :</span>
            <div className="saved-filters-buttons">
              {savedFilters.map(filter => (
                <div key={getSavedFilterKey(filter)} className="saved-filter-item">
                  <button
                    type="button"
                    className="saved-filter-btn"
                    onClick={() => handleApplySavedFilter(filter)}
                  >
                    <span className="saved-filter-name">{filter.name}</span>
                    <span
                      className="saved-filter-close"
                      role="button"
                      tabIndex={0}
                      aria-label={`Supprimer le filtre ${filter.name}`}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        handleDeleteSavedFilter(filter);
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                          event.stopPropagation();
                          event.preventDefault();
                          handleDeleteSavedFilter(filter);
                        }
                      }}
                    >
                      &times;
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="results-container">
          {loading ? (
            <div className="loading-indicator">Chargement des conférences...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : viewMode === 'card' ? (
            <div className="card-grid conference-card-grid">
              {conferences.length === 0 ? (
                <div className="no-results">Aucune conference trouvee</div>
              ) : (
                conferences.map((conf, index) => {
                  const rawId = conf._id || conf.id || '';
                  const normalizedId = rawId ? String(rawId) : `conf-card-${index}`;
                  const attendanceKey = rawId ? String(rawId) : null;
                  const isAttending = typeof conf.isAttending === 'boolean'
                    ? conf.isAttending
                    : (attendanceKey ? Boolean(attendedConferenceMap[attendanceKey]) : false);
                  return (
                    <ConferenceCard
                      key={normalizedId}
                      conference={conf}
                      isAttending={isAttending}
                      onToggleAttendance={handleToggleConferenceAttendance}
                    />
                  );
                })
              )}
            </div>
          ) : (
            <DataTable
              data={conferences}
              conferenceMode={true}
              attendedConferenceMap={attendedConferenceMap}
              onToggleConferenceAttendance={handleToggleConferenceAttendance}
            />
          )}
        </div>
      </div>

      <DefineFilterModal
        open={defineModalOpen}
        options={filterOptions}
        initialFilters={filters}
        onClose={handleCloseDefineFilter}
        onSave={handleSaveDefinedFilter}
        saving={savingDefineFilter}
        errorMessage={defineFilterError}
      />
    </div>
  );
};

export default TestPage;
